import { Button, CircularProgress, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { SkipNext as AvSkipNext } from '@material-ui/icons';
import { Map, OrderedMap } from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';

import { checkSendAccess } from 'shared/common/email-access';
import { getMediaFileKey } from 'shared/common/file-storage';
import { interpolateEmailTemplate } from 'shared/common/interpolate-template';
import { getSchedulingLink } from 'shared/common/scheduling-link-utils';
import { EmailAccountView } from 'shared/models/email-account';
import { EmailTemplateView } from 'shared/models/email-templates';
import { hasFlag, JOB_FLAG_INTRO_EMAILS_SUPPORTED } from 'shared/models/job';
import {
    awaitingClientStage,
    beingScheduledStage,
    clientFirstRoundStage,
    JobStageData,
    responseReceivedStage,
    rocketScreenCompleteStage,
    rocketScreenScheduledStage
} from 'shared/models/job-stages';
import { Permissions } from 'shared/models/permission';
import { getSequenceVariables, interpolate, SequenceVariables } from 'shared/models/sequence';
import { SequenceRecipientCustomStage } from 'shared/models/sequence-recipient';
import { UserData } from 'shared/models/user';

import { EmailAddress } from 'shared/types/email-compose';
import { FilePayload } from 'shared/types/file-payload';

import {
    fetchJobInfo,
    fetchJobsData,
    fetchPersonContacts,
    fetchUsers,
    getAllEmailAccountInfo,
    getEmailTemplates,
    getJobEmails,
    getSchedulingDrafts,
    moveCandidateToStage,
    sendSequenceToPerson,
    showModalAlert
} from '../actions';
import { getAccountOptions } from '../common/email/account-options';
import { EmailKind } from '../common/email/header-validator';
import { getEmailForOutreach } from '../common/person';
import {
    composeEmailWithValidation,
    EmailValidationData,
    replyToEmailWithValidation,
    replyToEmailWithValidationWithDifferentAccount
} from '../email-compose/actions';
import { ComposeEmailWindowData } from '../email-compose/types';
import { getLastCommunicationWithCandidate } from '../lib/last-communication-with-candidate';
import {
    Candidate,
    Client,
    Communication,
    Contact,
    EmailAccount,
    Job,
    List,
    ListState,
    Person,
    PersonDetails,
    RequestErrors,
    State
} from '../state';
import { IntroCandidateClientPopover } from './intro-candidate-client-popover';
import { SchedulingSendDialog } from './scheduling-send-dialog';
import { SchedulingSequenceMenuOption } from './scheduling-sequence-menu-option';
import { SubmitToClientPopover } from './submit-to-client-popover';

interface OwnProps {
    candidate: Candidate;
}

interface ConnectedProps {
    emailContentBlacklistedDomains: string;
    clients: List<Client>;
    communications: Map<string, Communication[]>;
    jobs: OrderedMap<string, Job>;
    jobEmails: Map<string, EmailAccount[]>;
    pendingRequests: Map<string, RequestErrors>;
    persons: List<Person>;
    emailAccounts: Map<string, EmailAccount>;
    jobStages: JobStageData[];
    contacts: Map<string, Contact[]>;
    users: Map<string, UserData>;
    listsState: Map<string, ListState>;
    emailTemplates: Map<string, EmailTemplateView[]>;
    sessionUser: UserData;
    details: List<PersonDetails>;
    userPermissions: Permissions;
}

interface ConnectedDispatch {
    getJobEmails: (jobId: string) => void;
    moveCandidateToStage: (candidate: Candidate, stage: string) => void;
    sendSequenceToPerson: (
        personId: string,
        jobId: string,
        account: string,
        sequenceId: string,
        variables: SequenceVariables,
        customStages: SequenceRecipientCustomStage[]
    ) => void;
    fetchJobInfo: (jobId: string) => void;
    getAllEmailAccountInfo: () => void;
    composeEmailWithValidation: (
        data: ComposeEmailWindowData,
        emailKind: EmailKind,
        validationData: EmailValidationData
    ) => void;
    replyToEmailWithValidation: (
        accountOptions: EmailAddress[],
        emailContentBlacklistedDomains: string,
        message: Communication,
        replyAll: boolean,
        emailKind: EmailKind,
        validationData: EmailValidationData,
        archiveOnReply?: boolean,
        composeWindowData?: Partial<ComposeEmailWindowData>,
        initialBody?: string
    ) => void;
    replyToEmailWithValidationWithDifferentAccount: (
        accountOptions: EmailAddress[],
        emailContentBlacklistedDomains: string,
        message: Communication,
        newAccount: EmailAddress,
        replyAll: boolean,
        emailKind: EmailKind,
        validationData: EmailValidationData,
        composeWindowData?: Partial<ComposeEmailWindowData>
    ) => void;
    fetchPersonContacts: (personId: string) => void;
    getEmailTemplates: (group: string) => void;
    fetchUsers: () => void;
    fetchJobsData: () => void;
    showModalAlert: (description: string | JSX.Element, title: string) => void;
    getSchedulingDrafts: (personId: string, jobId: string, linkType: 'calendar_only' | 'astra_only' | 'both') => any;
}

type MoveCandidateForwardButtonProps = OwnProps & ConnectedProps & ConnectedDispatch;

interface MoveCandidateForwardButtonState {
    schedulingSequencePreview: {
        initialCustomStages: SequenceRecipientCustomStage[];
        open: boolean;
        account: string;
        variables: SequenceVariables;
    };
    optionsOpen: boolean;
    isInTimeout: boolean;
}

class MoveCandidateForwardButtonComponent extends React.Component<
    MoveCandidateForwardButtonProps,
    MoveCandidateForwardButtonState
> {
    private anchorRef: React.RefObject<HTMLDivElement>;

    constructor(props: MoveCandidateForwardButtonProps) {
        super(props);
        this.state = {
            isInTimeout: false,
            optionsOpen: false,
            schedulingSequencePreview: {
                account: undefined,
                initialCustomStages: [],
                open: false,
                variables: undefined
            }
        };
        this.anchorRef = React.createRef();
        this.ensureDataExists(props);
    }

    ensureDataExists = (props: MoveCandidateForwardButtonProps) => {
        const { candidate, jobs, jobEmails, emailAccounts, listsState, emailTemplates, contacts } = props;
        const { jobId, personId } = candidate;
        const job = jobs.get(jobId);
        if (!job) {
            props.fetchJobInfo(jobId);
        }
        if (!jobEmails.get(jobId)) {
            props.getJobEmails(jobId);
        }
        if (emailAccounts.isEmpty()) {
            props.getAllEmailAccountInfo();
        }

        if (!emailTemplates.get(rocketScreenCompleteStage)) {
            props.getEmailTemplates(rocketScreenCompleteStage);
        }
        if (listsState.get('users') !== 'initialized') {
            props.fetchUsers();
        }
        if (!contacts.get(personId)) {
            props.fetchPersonContacts(personId);
        }
        if (listsState.get('jobs') !== 'initialized') {
            props.fetchJobsData();
        }
    };

    componentDidUpdate() {
        this.ensureDataExists(this.props);
    }

    checkCanReplyToEmail() {
        const { candidate, sessionUser, details, jobStages, userPermissions } = this.props;
        const currentStage = jobStages.find((s) => s.name === candidate.stage);

        if (currentStage.nextStage === rocketScreenCompleteStage) {
            const { account, lastEmail } = this.getLastEmail();
            let jobs: Job[];
            let personCandidates: Candidate[];
            if (lastEmail) {
                const jobIds = lastEmail.jobIds || [];
                jobs = jobIds.map((j) => this.props.jobs.get(j));
                personCandidates = details.list
                    .get(candidate.personId)
                    .candidates.filter((c) => jobIds.includes(c.jobId));
            } else {
                const job = this.props.jobs.get(candidate.jobId);
                jobs = [job];
                personCandidates = [candidate];
            }
            if (account) {
                return checkSendAccess(sessionUser, userPermissions, account, {
                    jobs,
                    personsCandidates: personCandidates
                });
            } else {
                return false;
            }
        } else {
            return true;
        }
    }

    getLastEmail = () => {
        const { candidate, jobs, emailAccounts, communications, users } = this.props;
        const { jobId, personId } = candidate;
        const job = jobs.get(jobId);
        const personCommunications = communications.get(personId);
        if (!personCommunications || !job || emailAccounts.isEmpty()) {
            return {};
        }
        const lastEmail = getLastCommunicationWithCandidate(personCommunications, emailAccounts, candidate);
        let account: EmailAccountView;
        if (lastEmail) {
            const lastEmailAccount = emailAccounts.get(lastEmail.account);
            if (lastEmailAccount.syncStatus === 'enabled') {
                account = lastEmailAccount;
            } else if (lastEmailAccount.syncStatus === 'archived') {
                const assignedUser = users.find((u) => u.id === candidate.assignee);
                const accountOptions = getAccountOptions(emailAccounts, assignedUser, undefined);
                account = emailAccounts.get(accountOptions?.[0]?.address);
            } else {
                account = undefined;
            }
        }
        return { lastEmail, account };
    };

    handleProceed = (stage?: string) => (event: React.SyntheticEvent<{}>) => {
        event.preventDefault();
        this.moveCandidate(stage);
        const timeout = 1000;
        this.setState({ isInTimeout: true, optionsOpen: false });
        setTimeout(() => {
            this.setState({ isInTimeout: false });
        }, timeout);
    };

    handleOpenSchedulingDialog = async (linkType: 'calendar_only' | 'astra_only' | 'both') => {
        const { candidate, persons, jobs, clients, users } = this.props;
        const { personId, jobId } = candidate;
        this.setState({ optionsOpen: false });

        const { drafts } = await this.props.getSchedulingDrafts(personId, jobId, linkType);

        const { lastEmail, account } = this.getLastEmail();
        const person = persons.list.get(personId);
        const job = jobs.get(jobId);
        const client = clients.list.get(job?.clientId);
        const assignee = users.get(candidate.assignee);
        const senderAccount = account;

        if (drafts.length > 0 && person && job && client && senderAccount && assignee) {
            const variables = getSequenceVariables({
                client,
                job,
                person,
                phoneCallSchedulingLink: assignee.calendarLink,
                senderAccount,
                user: assignee
            });
            // Keep aiRecruiterCallLink as placeholder — resolved at send time
            variables.aiRecruiterCallLink = '{{aiRecruiterCallLink}}';
            const defaultSubject = lastEmail ? `Re: ${lastEmail.subject.replace(/^Re:?\s*/i, '').trim()}` : '';
            const initialCustomStages = drafts.map((draft: { body: string; subject: string }) => ({
                body: interpolate(draft.body, variables),
                subject: draft.subject || defaultSubject
            }));
            this.setState({
                schedulingSequencePreview: {
                    account: senderAccount.email,
                    initialCustomStages,
                    open: true,
                    variables
                }
            });
        } else if (person && job && client && senderAccount && assignee) {
            if (linkType !== 'calendar_only') {
                this.props.showModalAlert(
                    'Scheduling drafts are not available yet. Please try again shortly. If this persists after 5 minutes, please report the issue.',
                    'Error'
                );
                return;
            }

            // Fallback: use aiDraftedReply from rr-email-response as Stage 1 if available,
            // otherwise empty custom stages lets SequencePreview render from job's actual sequence
            const subject = lastEmail ? `Re: ${lastEmail.subject.replace(/^Re:?\s*/i, '').trim()}` : '';
            const variables = getSequenceVariables({
                client,
                job,
                person,
                phoneCallSchedulingLink: assignee.calendarLink,
                senderAccount,
                subject,
                user: assignee
            });
            const initialCustomStages = lastEmail?.aiDraftedReply ? [{ body: lastEmail.aiDraftedReply, subject }] : [];
            this.setState({
                schedulingSequencePreview: {
                    account: senderAccount.email,
                    initialCustomStages,
                    open: true,
                    variables
                }
            });
        } else if (!senderAccount) {
            this.props.showModalAlert(
                'No email account found for this candidate. Please ensure there is an email thread with an active account.',
                'Cannot Open Scheduling'
            );
        } else {
            this.props.showModalAlert(
                'Missing required data to open scheduling dialog. Please ensure the candidate has an assigned recruiter and valid job.',
                'Cannot Open Scheduling'
            );
        }
    };

    handleSchedulingClick = () => {
        this.handleOpenSchedulingDialog('calendar_only');
    };

    handleOpenCalendarClick = (schedulingLink: string) => () => {
        const width = 1280;
        const height = 800;
        const pixelOffset = 50;
        const top = window.screenY + pixelOffset;
        const left = window.screenX + pixelOffset;

        const { personId, jobId } = this.props.candidate;
        const client = this.props.clients.list.get(this.props.jobs.get(jobId).clientId);
        const { link: modifiedSchedulingLink } = getSchedulingLink(schedulingLink, personId, jobId, client.name);
        window.open(
            modifiedSchedulingLink,
            '_blank',
            `location=no, toolbar=no, menubar=no,\
             height=${height}, width=${width}, top=${top}, left=${left}`
        );
        this.setState({ optionsOpen: false });
    };

    handleAIInterviewClick = () => {
        this.handleOpenSchedulingDialog('astra_only');
    };

    handleAIInterviewAndSchedulingClick = () => {
        this.handleOpenSchedulingDialog('both');
    };

    handleOptionsOpen = () => {
        this.setState({ optionsOpen: true });
    };

    handleOptionsClose = () => {
        this.setState({ optionsOpen: false });
    };

    hidePhoneCallScheduling = () => {
        this.setState({
            schedulingSequencePreview: {
                account: undefined,
                initialCustomStages: [],
                open: false,
                variables: undefined
            }
        });
    };

    handleSendSequence = (data: { sequenceId: string; customStages: SequenceRecipientCustomStage[] }) => {
        const { account, variables } = this.state.schedulingSequencePreview;
        const { sequenceId, customStages } = data;
        this.props.moveCandidateToStage(this.props.candidate, beingScheduledStage);
        this.props.sendSequenceToPerson(
            this.props.candidate.personId,
            this.props.candidate.jobId,
            account,
            sequenceId,
            variables,
            customStages
        );
        this.setState({
            schedulingSequencePreview: {
                account: undefined,
                initialCustomStages: [],
                open: false,
                variables: undefined
            }
        });
    };

    moveCandidate = (stage: string) => {
        const { candidate, jobs, users, emailAccounts, clients, persons, emailTemplates, contacts, sessionUser } =
            this.props;
        const { jobId, personId } = candidate;
        const job = jobs.get(jobId);
        const personContacts = contacts.get(personId);
        const person = persons.list.get(personId);
        if (stage === rocketScreenCompleteStage && candidate.stage === rocketScreenScheduledStage) {
            this.props.moveCandidateToStage(candidate, stage);
            if (!this.checkCanReplyToEmail()) {
                return;
            }
            const templateName = 'Rocket Screen Followup';
            const template = emailTemplates.get(rocketScreenCompleteStage).find((t) => t.kind === templateName);
            const client = clients.list.get(job.clientId);
            const assigneeUser = users.get(candidate.assignee);
            const validationData: EmailValidationData = {
                client,
                emailAccounts: emailAccounts.valueSeq().toArray(),
                job,
                personContacts,
                users: users.valueSeq().toArray()
            };
            const emailKind: EmailKind = 'candidate';
            const attachments: FilePayload[] = job.onePager
                ? [
                      {
                          filename: job.onePager.key.split('/').reverse()[0],
                          key: getMediaFileKey(job.onePager.key),
                          size: job.onePager.size,
                          type: 's3Key'
                      }
                  ]
                : [];
            const commonComposeData = {
                attachments,
                canEditToRecipients: true,
                draftSavedAt: null as number,
                isClientComm: false,
                jobId,
                personId,
                windowId: `move-to-rsc-${personId}-${jobId}`
            };

            const { account: suggestedAccount, lastEmail } = this.getLastEmail();
            const defaultAccount =
                lastEmail && lastEmail.account === suggestedAccount.email
                    ? { address: lastEmail.account, name: lastEmail.accountInfo.name.full }
                    : suggestedAccount
                      ? { address: suggestedAccount.email, name: suggestedAccount.name.full }
                      : undefined;
            const accountOptions = getAccountOptions(emailAccounts, sessionUser, jobId, defaultAccount);
            if (lastEmail && lastEmail.account === defaultAccount.address) {
                const body = interpolateEmailTemplate(template.body, {
                    client,
                    job,
                    person,
                    senderAccount: emailAccounts.get(lastEmail.account),
                    senderName: assigneeUser.name,
                    user: assigneeUser
                });
                this.props.replyToEmailWithValidation(
                    accountOptions,
                    this.props.emailContentBlacklistedDomains,
                    lastEmail,
                    true,
                    emailKind,
                    validationData,
                    true,
                    commonComposeData,
                    body
                );
            } else if (lastEmail && lastEmail.account !== defaultAccount.address) {
                this.props.replyToEmailWithValidationWithDifferentAccount(
                    accountOptions,
                    this.props.emailContentBlacklistedDomains,
                    lastEmail,
                    defaultAccount,
                    true,
                    emailKind,
                    {
                        client,
                        emailAccounts: emailAccounts.valueSeq().toArray(),
                        job,
                        personContacts,
                        users: users.valueSeq().toArray()
                    },
                    {
                        canEditToRecipients: true,
                        isClientComm: lastEmail.isClientComm,
                        jobId: job ? job.id : undefined,
                        personId: person.id
                    }
                );
            } else {
                const account = emailAccounts.get(accountOptions[0]?.address);
                const address = getEmailForOutreach(personContacts, personId, job.contactTypes);
                const to = address ? [{ name: person.name.full, address }] : [];
                const body = interpolateEmailTemplate(template.body, {
                    client,
                    job,
                    person,
                    senderAccount: account,
                    senderName: assigneeUser.name,
                    user: assigneeUser
                });
                this.props.composeEmailWithValidation(
                    {
                        ...commonComposeData,
                        account: { name: account.name.full, address: account.email },
                        accountOptions,
                        body,
                        headers: {
                            bcc: template.bcc,
                            cc: template.cc,
                            subject: template.subject,
                            to
                        },
                        threadId: null
                    },
                    emailKind,
                    validationData
                );
            }

            return;
        } else {
            this.props.moveCandidateToStage(candidate, stage);
        }
    };

    render() {
        const {
            candidate,
            clients,
            emailAccounts,
            emailTemplates,
            contacts,
            jobs,
            jobStages,
            listsState,
            pendingRequests,
            users
        } = this.props;
        const { schedulingSequencePreview } = this.state;
        const { personId, jobId } = candidate;
        const job = jobs.get(jobId);
        const { optionsOpen } = this.state;
        const currentStage = jobStages.find((stage) => stage.name === candidate.stage);
        const nextStage = jobStages.find((stage) => stage.name === currentStage.nextStage);
        const { lastEmail } = this.getLastEmail();

        let proceedOptions;
        let proceedForPhoneCallScheduling;

        const hasAllData =
            listsState.get('users') === 'initialized' &&
            listsState.get('jobs') === 'initialized' &&
            !emailAccounts.isEmpty() &&
            !!emailTemplates.get(rocketScreenCompleteStage) &&
            !!contacts.get(personId);

        if (candidate.stage === responseReceivedStage && job.phoneCallRequired) {
            const schedulingLink = users.find((u) => u.id === candidate.assignee).calendarLink;
            const mixmaxCalendarOption = schedulingLink ? (
                <MenuItem onClick={this.handleOpenCalendarClick(schedulingLink)}>Manually book time</MenuItem>
            ) : null;
            const astraLink = (
                <MenuItem
                    onClick={this.handleAIInterviewClick}
                    disabled={pendingRequests.has(`get-scheduling-drafts-${personId}-${jobId}`)}
                >
                    Send AI Interview
                </MenuItem>
            );
            const aiInterviewAndSchedulingLink = (
                <MenuItem
                    onClick={this.handleAIInterviewAndSchedulingClick}
                    disabled={pendingRequests.has(`get-scheduling-drafts-${personId}-${jobId}`)}
                >
                    Send AI Interview + Scheduling Link
                </MenuItem>
            );
            proceedForPhoneCallScheduling = schedulingSequencePreview.open ? (
                <SchedulingSendDialog
                    jobId={jobId}
                    personId={personId}
                    initialCustomStages={schedulingSequencePreview.initialCustomStages}
                    variables={schedulingSequencePreview.variables}
                    onClose={this.hidePhoneCallScheduling}
                    onSend={this.handleSendSequence}
                />
            ) : null;
            proceedOptions = (
                <Menu
                    open={optionsOpen}
                    anchorEl={this.anchorRef.current}
                    onClose={this.handleOptionsClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <SchedulingSequenceMenuOption
                        candidate={candidate}
                        text="Send Scheduling Sequence"
                        onClick={this.handleSchedulingClick}
                        hasLastEmail={!!lastEmail}
                    />
                    {astraLink}
                    {aiInterviewAndSchedulingLink}
                    {mixmaxCalendarOption}
                    <MenuItem onClick={nextStage ? this.handleProceed(nextStage.name) : null} disabled={!nextStage}>
                        Skip and move forward
                    </MenuItem>
                </Menu>
            );
        } else if (nextStage && nextStage.name === awaitingClientStage) {
            proceedOptions = (
                <SubmitToClientPopover
                    open={optionsOpen}
                    anchorEl={this.anchorRef.current}
                    onRequestClose={this.handleOptionsClose}
                    client={clients.list.get(candidate.clientId)}
                    jobId={candidate.jobId}
                    personId={candidate.personId}
                />
            );
        } else if (nextStage && nextStage.name === clientFirstRoundStage) {
            if (
                hasFlag(job, JOB_FLAG_INTRO_EMAILS_SUPPORTED) &&
                (job.introSettings.emailTo || job.templates.preIntroduction)
            ) {
                proceedOptions = (
                    <IntroCandidateClientPopover
                        open={optionsOpen}
                        anchorEl={this.anchorRef.current}
                        onRequestClose={this.handleOptionsClose}
                        jobId={candidate.jobId}
                        personId={candidate.personId}
                    />
                );
            }
        }

        const isWaitingForUpdate = pendingRequests.has(`candidate-update-${personId}-${jobId}`);
        const isButtonDisabled = this.state.isInTimeout || isWaitingForUpdate;

        const spinnerSize = 20;
        const spinnerThickness = 2;
        let button: JSX.Element;
        const buttonClassName = 'candidate-move-button';
        if (isButtonDisabled) {
            const spinner = <CircularProgress size={spinnerSize} thickness={spinnerThickness} />;
            button = <div className={`${buttonClassName} disabled`}>{spinner}</div>;
        } else {
            const proceedFunction = proceedOptions
                ? this.handleOptionsOpen
                : nextStage
                  ? this.handleProceed(nextStage.name)
                  : null;
            const rocketScreenComplete = jobStages.find((j) => j.name === rocketScreenCompleteStage);
            const canReplyToEmail = !lastEmail || this.checkCanReplyToEmail();
            const amRestricted = !job.accountManagerId && nextStage && nextStage.id > rocketScreenComplete.id;
            const disabled = !nextStage || !hasAllData || !canReplyToEmail || amRestricted;
            let tooltip = '';
            if (disabled) {
                if (!canReplyToEmail) {
                    tooltip = 'You do not have access to send an email to the candidate.';
                } else if (amRestricted) {
                    tooltip = 'Cannot move past Rocket Screen Complete without an account manager';
                }
            } else {
                if (nextStage && nextStage.name !== awaitingClientStage) {
                    tooltip = `Move forward to ${nextStage.label}`;
                }
            }
            const buttonWithoutTooltip = (
                <Button
                    aria-label="Move candidate forward"
                    className={buttonClassName}
                    color="primary"
                    disabled={disabled}
                    onClick={proceedFunction}
                    variant="contained"
                    fullWidth={true}
                >
                    <AvSkipNext />
                </Button>
            );

            button = tooltip ? (
                <Tooltip title={tooltip}>
                    <span>{buttonWithoutTooltip}</span>
                </Tooltip>
            ) : (
                buttonWithoutTooltip
            );
        }

        return (
            <div className="candidate-move-button-container candidate-move-button-container-big" ref={this.anchorRef}>
                {button}
                {proceedOptions}
                {proceedForPhoneCallScheduling}
            </div>
        );
    }
}

const mapStateToProps = (state: State): ConnectedProps => ({
    clients: state.clients,
    communications: state.communications,
    contacts: state.contacts,
    details: state.personsDetails,
    emailAccounts: state.emailAccounts,
    emailContentBlacklistedDomains: state.appConstants.constants.emailContentBlacklistedDomains,
    emailTemplates: state.emailTemplates,
    jobEmails: state.jobEmails,
    jobStages: state.appConstants.constants.jobStages,
    jobs: state.jobs,
    listsState: state.listsState,
    pendingRequests: state.pendingRequests,
    persons: state.persons,
    sessionUser: state.session.user,
    userPermissions: state.session.userPermissions,
    users: state.users
});
const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    composeEmailWithValidation,
    fetchJobInfo,
    fetchJobsData,
    fetchPersonContacts,
    fetchUsers,
    getAllEmailAccountInfo,
    getEmailTemplates,
    getJobEmails,
    getSchedulingDrafts,
    moveCandidateToStage,
    replyToEmailWithValidation,
    replyToEmailWithValidationWithDifferentAccount,
    sendSequenceToPerson,
    showModalAlert
};

export const MoveCandidateForwardButton = connect<ConnectedProps, ConnectedDispatch, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
)(MoveCandidateForwardButtonComponent);
